
import {
  computed,
  defineComponent,
  onMounted,
  PropType,
  ref,
  shallowRef,
  toRef,
  watch,
} from "vue";
import { IBulkDelete } from "@/interfaces/iPageModel";
import Datatable from "@/components/kt-datatable/KTDatatable.vue";
import { TranslationService } from "@/core/services/TranslationService";
import { INNER_PAGE_ROWS_PER_PAGE, usePagination } from "@/helpers/pagination";
import { IAddEditFormModel } from "@/interfaces/iAddEditFormModel";
import { FormatMethods } from "@/helpers/data-format";
import { EventManagementAPI } from "@/services/api/EventManagement.service";
import {
  IEventManagements,
  IEventManagementFormModel,
} from "@/interfaces/iEventManagement";
import { getLookups } from "@/services/api/Lookup.service";
import {
  EventManagementFormTableBulkDelete,
  EventManagementFormTableFilters,
  EventManagementFormTableHeaders,
  EVENT_MANAGEMENT_FORM_LOOKUP,
} from "../dataTableOptions";
import { useToast } from "vue-toastification";
import * as Yup from "yup";
import { IForm } from "@/interfaces/iForm";
import { FormApi } from "@/services/api/Form.service";
import { ITabConfig } from "@/interfaces/iTabConfig";
import { FORM_TABS } from "@/views/form/dataTableOptions";
import PreviewLink from "@/components/typography/PreviewLink.vue";
import FormDetailModal from "@/views/form/modal/FormDetailModal.vue";
import { debounce } from "lodash";
interface ITabsEmit {
  tabs: ITabConfig[];
  title: string;
  icon?: string;
}
type Events = {
  VZTabsUpdate: ITabsEmit;
};
export default defineComponent({
  props: {
    data: { type: Object as PropType<IEventManagements>, required: true },
    openDrawer: { type: Boolean, default: false },
    isActive: { type: Boolean, default: false },
  },
  components: {
    Datatable,
    PreviewLink,
    FormDetailModal,
  },
  setup(props) {
    const t = (key: string) => {
      return TranslationService.translate(key);
    };
    let options: { label: any; value: string }[] = [];
    const dataProp = toRef(props, "data");
    const openDrawer = toRef(props, "openDrawer");
    const showDeleteDialog = ref<any>(false);
    const { page, total } = usePagination();
    const rowsPerPage = ref(INNER_PAGE_ROWS_PER_PAGE);
    const loading = ref(false);
    const defaultFilter = ref({ status: "ACTIVE" });
    const showDetailDialog = ref<any>(false);
    const showSearch = true;
    const tableData = ref<IEventManagementFormModel[]>([]);
    const selectedForAction = ref<Partial<IEventManagements>>({});
    const selectedForm = ref<Partial<IForm>>({});
    const localPreference = ref({});
    const bulkDelete = ref<IBulkDelete>(EventManagementFormTableBulkDelete());
    const tableHeader = ref(EventManagementFormTableHeaders());
    const tableFilter = ref(EventManagementFormTableFilters());
    const toast = useToast();
    const formDeleteMessage = t("pages.event.formDeleteMessage");
    const eventAssignMessage = t("pages.form.formAssignSuccess");
    const formDetailLoader = ref(false);
    const Datatable = ref();
    const format = computed(() => {
      return FormatMethods;
    });
    const tabs = shallowRef<ITabConfig[]>(FORM_TABS());
    const viewName = "eventManagementForm";
    let permissionModel = ref<any>({
      create: "CAN-NOT-CREATE",
      delete: "CAN-NOT-DELETE",
    });
    onMounted(async () => {
      // await refreshList();
      loading.value = true;
      await getLookups(EVENT_MANAGEMENT_FORM_LOOKUP.url).then((res) => {
        res.map((data) => {
          options.push({
            label: data.text,
            value: data.value,
          });
        });
      });
    });

    let formData = ref<IAddEditFormModel>({
      page: "event",
      header: "EventManagement Form",
      edit: false,
      data: null,
      validationSchema: Yup.object({
        formDefinitionId: Yup.array().required(
          t("pages.event.form") + " " + t("common.isRequired")
        ),
      }),
      label: [{ addHeader: t("pages.form.assignForm") }],
      model: [
        {
          name: "formDefinitionId",
          label: t("pages.event.form"),
          type: "list",
          required: true,
          showOnlyInEdit: false,
          disabled: false,
          placeholder: "Please select form",
          options: options,
          multiSelect: true,
        },
      ],
    });
    const edit = (item: any) => {
      formData.value.data = { ...item };
      formData.value.edit = true;
    };

    const submit = async (reqData) => {
      const data = reqData.formDefinitionId.map((item) => {
        return {
          formDefinitionId: item,
          eventId: dataProp.value.webId,
        };
      });
      loading.value = true;
      await EventManagementAPI.getInstance()
        .addEventManagementForm(data, formData.value.edit)
        .then(() => {
          toast.success(`${eventAssignMessage}`);
          queryUpdate(dataProp.value.webId, { ...localPreference.value });
        })
        .catch(() => {
          //
        })
        .finally(() => {
          loading.value = false;
        });
    };

    const syncQuery = async (params?: Record<string, number | string>) => {
      queryUpdate(dataProp.value.webId, params);
    };

    const queryUpdate = async (
      webId: number,
      params?: Record<string, number | string>
    ) => {
      try {
        loading.value = true;
        if (!params || !params.status) {
          params = {
            ...params,
            status: "ACTIVE",
          };
        }
        if (!params || !params.sort) {
          params = {
            ...params,
            sort: "createdDate,desc",
          };
        }
        const data =
          await EventManagementAPI.getInstance().getEventManagementFormList(
            dataProp.value.webId,
            {
              ...params,
            }
          );
        localPreference.value = { ...params };
        tableData.value = [...data.content];
        total.value = data.totalElements;
        rowsPerPage.value = data.size;
        page.value = ++data.number;
      } finally {
        loading.value = false;
      }
    };

    const openDetailModal = async (event) => {
      showDetailDialog.value = true;
      try {
        formDetailLoader.value = true;
        selectedForm.value = {};
        const form = await FormApi.getInstance().getFormById(event.webId);
        selectedForm.value = form;
      } catch (error) {
        //
      } finally {
        formDetailLoader.value = false;
      }
    };

    const onBulkDelete = async (formDefinitionIds) => {
      try {
        await EventManagementAPI.getInstance()
          .deleteEventManagementForm(dataProp.value.webId, formDefinitionIds)
          .then(() => {
            toast.success(formDeleteMessage);
          })
          .catch((err) => {
            console.error(err);
          });
        refreshList();
      } catch (e) {
        //
      }
    };
    const refreshList = () => {
      showDeleteDialog.value = false;
      queryUpdate(dataProp.value.webId, { ...localPreference.value });
    };

    const webId = computed(() => props.data?.webId);
    let lastFetchedWebId = null;

    const debouncedQueryUpdate = debounce(async (webId) => {
      if (webId !== lastFetchedWebId) {
        tableData.value = [];
        lastFetchedWebId = webId;
        defaultFilter.value = { ...defaultFilter.value };
        rowsPerPage.value = INNER_PAGE_ROWS_PER_PAGE;
        await queryUpdate(+webId, {
          ...defaultFilter.value,
          page: page.value - 1,
          size: rowsPerPage.value,
        });
      }
    }, 300);

    watch(
      () => props.isActive,
      async (isActive) => {
        if (isActive && dataProp.value.webId) {
          // await queryUpdate(dataProp.value.webId, {
          //   ...defaultFilter.value,
          //   page: page.value - 1,
          //   size: rowsPerPage.value,
          // });
          loading.value = true;
          if (Datatable.value?.VZFilter) {
            await Datatable.value.VZFilter.getAppliedFilters();
          }
        } else {
          tableData.value = [];
        }
      }
    );
    return {
      bulkDelete,
      format,
      tableData,
      tableHeader,
      tableFilter,
      showSearch,
      page,
      total,
      rowsPerPage,
      defaultFilter,
      loading,
      selectedForAction,
      syncQuery,
      refreshList,
      formData,
      submit,
      viewName,
      edit,
      onBulkDelete,
      selectedForm,
      openDetailModal,
      t,
      tabs,
      formDetailLoader,
      showDeleteDialog,
      showDetailDialog,
      permissionModel,
      Datatable,
    };
  },
});
